import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { PencilIcon, XMarkIcon, CheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from './Button';

interface DigitalSignatureProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (signatureData: string, signerName: string, signerRole: string) => void;
  title?: string;
}

const DigitalSignature: React.FC<DigitalSignatureProps> = ({
  isOpen,
  onClose,
  onSave,
  title = 'Digital Signature'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerRole, setSignerRole] = useState('');
  const [hasSignature, setHasSignature] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Set canvas size based on display size
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * 2; // Increase resolution for better quality
        canvas.height = rect.height * 2;
        
        // Scale the canvas back down using CSS
        canvas.style.width = rect.width + 'px';
        canvas.style.height = rect.height + 'px';
        
        // Scale the drawing context so everything draws at 2x resolution
        ctx.scale(2, 2);
        
        // Set canvas background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, rect.width, rect.height);
        
        // Set drawing style
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }
  }, [isOpen]);

  const getEventPos = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    if (!canvas || !rect) return { x: 0, y: 0 };
    
    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0]?.clientX || e.changedTouches[0]?.clientX;
      clientY = e.touches[0]?.clientY || e.changedTouches[0]?.clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const pos = getEventPos(e);
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
      }
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const pos = getEventPos(e);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        setHasSignature(true);
      }
    }
  };

  const stopDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const rect = canvas.getBoundingClientRect();
        
        // Clear the canvas
        ctx.clearRect(0, 0, rect.width, rect.height);
        
        // Set white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, rect.width, rect.height);
        
        // Reset drawing style
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        setHasSignature(false);
      }
    }
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (canvas && hasSignature && signerName.trim() && signerRole.trim()) {
      const signatureData = canvas.toDataURL('image/png');
      onSave(signatureData, signerName.trim(), signerRole.trim());
      handleClose();
    }
  };

  const handleClose = () => {
    setSignerName('');
    setSignerRole('');
    clearSignature();
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
      onClick={(e) => {
        // Close modal if clicking on backdrop
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
              <div 
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4"
        >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={handleClose}
            className="rounded-md p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Signer Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 select-none">
                Aláíró neve
              </label>
              <input
                value={signerName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignerName(e.target.value)}
                placeholder="Teljes név"
                className="w-full rounded-md border border-gray-300 px-4 py-2 text-gray-900 placeholder-gray-500 bg-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 select-none">
                Pozíció/Szerepkör
              </label>
              <input
                value={signerRole}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignerRole(e.target.value)}
                placeholder="pl. Technikus, Ügyfél"
                className="w-full rounded-md border border-gray-300 px-4 py-2 text-gray-900 placeholder-gray-500 bg-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Signature Canvas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 select-none">
              Aláírás
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 select-none">
              <canvas
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                onTouchCancel={stopDrawing}
                className="w-full h-64 bg-white border border-gray-200 rounded cursor-crosshair select-none"
                style={{ 
                  touchAction: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none'
                }}
              />
              <div className="flex items-center justify-between mt-3">
                <p className="text-sm text-gray-500 flex items-center select-none">
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Húzza ide az aláírást
                </p>
                <button
                  onClick={clearSignature}
                  className="text-sm text-red-600 hover:text-red-700 flex items-center select-none"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-1" />
                  Törlés
                </button>
              </div>
            </div>
          </div>

          {/* Location and timestamp info */}
          <div className="bg-gray-50 p-3 rounded-lg select-none">
            <p className="text-sm text-gray-600">
              <strong>Időpont:</strong> {new Date().toLocaleString('hu-HU')}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              <strong>Hely:</strong> {navigator.geolocation ? 'Koordináták rögzítve' : 'Nem elérhető'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t">
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            Mégse
          </Button>
          <Button
            onClick={saveSignature}
            disabled={!hasSignature || !signerName.trim() || !signerRole.trim()}
            className="flex items-center"
          >
            <CheckIcon className="h-4 w-4 mr-2" />
            Aláírás mentése
          </Button>
        </div>
      </div>
    </div>
  );

  // Render the modal content into a portal at the document body
  return createPortal(modalContent, document.body);
};

export default DigitalSignature; 